import {
	Avatar,
	Card,
	CardContent,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Stack,
	TextField,
	Typography,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
} from "@mui/material";
import { useState } from "react";
import { useUserDetailQuery } from "Clients/Api/Queries/useUserDetailQuery";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyIcon from "@mui/icons-material/Key";
import PasswordIcon from "@mui/icons-material/Password";
import { AppRouting, getPath } from "Infrastructure/Utils/UrlUtils";
import { useUserDeleteMutation } from "Clients/Api/Mutations/useUserDeleteMutation";
import { useUserUpdateMutation } from "Clients/Api/Mutations/useUserUpdateMutation";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import {
	AccessRoleDto,
	SetPasswordCommand,
	UserDetailDto,
} from "Infrastructure/Api/Api";
import { useNavigate } from "react-router-dom";
import { BlDefaultButton } from "Shared/Buttons/BlDefaultButton";
import { usePasswordSetMutation } from "Auth/Api/Mutations/usePasswordSetMutation";

type Props = { id: number };

export const UserDetail: React.FunctionComponent<Props> = (props) => {
	const { id } = props;

	const { data, isLoading, error } = useUserDetailQuery(id);

	const userDeleteMutation = useUserDeleteMutation();
	const userUpdateMutation = useUserUpdateMutation();
	const passwordSetMutation = usePasswordSetMutation();
	const navigate = useNavigate();

	const [isEditing, setIsEditing] = useState(false);
	const [editData, setEditData] = useState<UserDetailDto | null>(null);

	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);

	const handleGeneratePassword = () => {
		const length = 16;
		const charset =
			"abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
		let generatedPassword = "";
		for (let i = 0, n = charset.length; i < length; ++i) {
			generatedPassword += charset.charAt(Math.floor(Math.random() * n));
		}
		setPassword(generatedPassword);
	};

	const toggleShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const deleteUser = () => {
		userDeleteMutation.mutate(id);
	};

	// TODO: Fetch selectable roles from API
	const selectableRoles: AccessRoleDto[] = [
		{ accessRoleID: 1, name: "Admin" },
		{ accessRoleID: 2, name: "Manager" },
		{ accessRoleID: 3, name: "Developer" },
		{ accessRoleID: 4, name: "Announcer" },
		{ accessRoleID: 5, name: "Guest" },
	];

	const toggleEdit = () => {
		if (!isEditing) {
			if (data?.data && "userID" in data.data) {
				setEditData(data.data as UserDetailDto);
			}
		} else {
			if (data?.data && "userID" in data.data) {
				setEditData(data.data as UserDetailDto);
			}
		}
		setIsEditing(!isEditing);
	};

	const handleInputChange = (field: keyof UserDetailDto, value: any) => {
		if (!editData) return;
		setEditData({ ...editData, [field]: value });
	};

	const handleRoleChange = (roleID: number) => {
		if (!editData) return;
		const selectedRole = selectableRoles.find(
			(role) => role.accessRoleID === roleID
		);
		if (selectedRole) {
			setEditData({ ...editData, accessRoles: [selectedRole] });
		}
	};

	const updateUser = () => {
		if (!editData) return;
		userUpdateMutation.mutate(editData, {
			onSuccess: (response) => {
				if (response.status === 200) {
					setEditData(response.data);
					setIsEditing(false);
				}
			},
		});
	};

	if (isLoading) {
		return <>...</>;
	}

	if (data?.status !== 200 || !!error) {
		return <>Uživatel nenalezen. {error?.message}</>;
	}

	const getPasswordSetDialog = () => {
		return (
			<Stack spacing={2} padding={2}>
				<Stack direction="row" alignItems="center" spacing={1}>
					<TextField
						label="Heslo"
						type={showPassword ? "text" : "password"}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						fullWidth
						size="small"
						autoComplete="new-password"
					/>

					<IconButton onClick={toggleShowPassword}>
						{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
					</IconButton>

					<IconButton onClick={handleGeneratePassword}>
						<PasswordIcon />
					</IconButton>
				</Stack>
			</Stack>
		);
	};

	const confirmSetPassword = () => {
		if (password) {
			passwordSetMutation.mutate(
				{ login: data?.data.login, password },
				{
					onSuccess: () => {
						setPassword("");
					},
				}
			);
		} else {
			alert("Passwords do not match!");
		}
	};

	return (
		<>
			<Card>
				<CardContent>
					<Stack direction="row" justifyContent="space-between">
						<Stack>
							<IconButton onClick={(_e) => navigate(getPath(AppRouting.Users))}>
								<ArrowBackIcon />
							</IconButton>
						</Stack>

						<Stack alignItems="center">
							<Avatar />
							<Typography variant="h2" mt={1}>
								{editData?.firstName && editData?.lastName
									? `${editData.firstName} ${editData.lastName}`
									: `${data?.data.firstName} ${data?.data.lastName}`}
							</Typography>
							<Typography variant="caption">{data?.data.login}</Typography>
						</Stack>

						<Stack direction="row" gap={1}>
							<BlDefaultButton onClick={toggleEdit}>
								{isEditing ? <EditOffIcon /> : <EditIcon />}
							</BlDefaultButton>
							<BlConfirmButton
								modalTitle={"Nastavení hesla"}
								modalContent={getPasswordSetDialog()}
								onResult={(result) => {
									if (!!result) {
										confirmSetPassword();
									}
								}}
							>
								<KeyIcon />
							</BlConfirmButton>
							<BlConfirmButton
								disabled={data?.data.accessRoles.some(
									(x) => x.name === "Admin"
								)}
								modalTitle={"Odstranění uživatele"}
								modalContent={
									"Opravdu si přejete odstranit uživatele? Tuto akci nelze vrátit zpět."
								}
								onResult={(result) => {
									if (!!result) {
										deleteUser();
									}
								}}
							>
								<DeleteIcon />
							</BlConfirmButton>
						</Stack>
					</Stack>
				</CardContent>
				<Divider />

				<List
					sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
				>
					{isEditing ? (
						<Stack gap={1.5} mt={2}>
							<ListItem>
								<FormControl fullWidth>
									<InputLabel size="small">Role</InputLabel>
									<Select
										value={editData?.accessRoles[0]?.accessRoleID || ""}
										size="small"
										label="Role"
										onChange={(e) => handleRoleChange(Number(e.target.value))}
									>
										{selectableRoles.map((role) => (
											<MenuItem
												key={role.accessRoleID}
												value={role.accessRoleID}
											>
												{role.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</ListItem>
							<ListItem>
								<FormControl fullWidth>
									<InputLabel size="small">Stav</InputLabel>
									<Select
										value={editData?.status ?? ""}
										size="small"
										label="Stav"
										onChange={(e) =>
											handleInputChange("status", e.target.value)
										}
									>
										<MenuItem value="Active">Active</MenuItem>
										<MenuItem value="Disabled">Disabled</MenuItem>
									</Select>
								</FormControl>
							</ListItem>
							<ListItem>
								<TextField
									fullWidth
									size="small"
									label="Jméno"
									value={editData?.firstName ?? ""}
									onChange={(e) =>
										handleInputChange("firstName", e.target.value)
									}
								/>
							</ListItem>
							<ListItem>
								<TextField
									fullWidth
									size="small"
									label="Příjmení"
									value={editData?.lastName ?? ""}
									onChange={(e) =>
										handleInputChange("lastName", e.target.value)
									}
								/>
							</ListItem>
							<ListItem>
								<TextField
									fullWidth
									size="small"
									label="E-mail"
									value={editData?.email ?? ""}
									onChange={(e) => handleInputChange("email", e.target.value)}
								/>
							</ListItem>
							<ListItem>
								<TextField
									size="small"
									fullWidth
									label="Telefon"
									value={editData?.phone ?? ""}
									onChange={(e) => handleInputChange("phone", e.target.value)}
								/>
							</ListItem>

							<Stack direction="row" mb={1} justifyContent="center">
								<Button
									variant="contained"
									color="primary"
									onClick={updateUser}
								>
									Aktualizovat
								</Button>
							</Stack>
						</Stack>
					) : (
						<Stack ml={1.5}>
							<ListItem>
								<ListItemText
									primary="Role"
									secondary={
										editData?.accessRoles?.map((x) => x.name).join(", ") ??
										data?.data.accessRoles?.map((x) => x.name).join(", ") ??
										"-"
									}
								/>
							</ListItem>
							<ListItem>
								<ListItemText
									primary="Stav"
									secondary={editData?.status ?? data?.data.status ?? "-"}
								/>
							</ListItem>
							<ListItem>
								<ListItemText
									primary="Jméno"
									secondary={editData?.firstName ?? data?.data.firstName ?? "-"}
								/>
							</ListItem>
							<ListItem>
								<ListItemText
									primary="Příjmení"
									secondary={editData?.lastName ?? data?.data.lastName ?? "-"}
								/>
							</ListItem>
							<ListItem>
								<ListItemText
									primary="E-mail"
									secondary={editData?.email ?? data?.data.email ?? "-"}
								/>
							</ListItem>
							<ListItem>
								<ListItemText
									primary="Telefon"
									secondary={editData?.phone ?? data?.data.phone ?? "-"}
								/>
							</ListItem>
						</Stack>
					)}
				</List>
			</Card>
		</>
	);
};
